/* eslint-disable no-alert */

import { Coords } from 'google-map-react';
import chainHull2D, { getCircleRadius, sortPointX, sortPointY } from './GeofenceMap.utils.math';

import colors from 'styles/colors';

// TODO: these have to be fixed. Module scoped mutable variables is crazy.
let circleGeofence = null;
let polygonGeofence = null;
const MAP_SETTINGS = {
  strokeColor: colors.fleetBlue,
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: colors.fleetBlue,
  fillOpacity: 0.35,
};

/**
 * Handles the autocompletion of the search input field as well as placing the marker on the map
 * once the search has been concluded
 */
export const searchAutocompleteHandler = (map, mapsApi, input) => {
  if (!input || !mapsApi || input instanceof HTMLInputElement === false) {
    return;
  }

  const autocomplete = new mapsApi.places.Autocomplete(input);

  autocomplete.bindTo('bounds', map);
  map.controls[mapsApi.ControlPosition.TOP_LEFT].push(input);

  const marker = new mapsApi.Marker({
    map,
    anchorPoint: new mapsApi.Point(0, -29),
  });

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();

    if (!place.geometry) {
      marker.setVisible(false);

      // User entered the name of a Place that was not suggested and
      // pressed the Enter key, or the Place Details request failed.
      window.alert(`No details available for input: '${place.name}'`);

      return;
    }

    // If the place has a geometry, then present it on a map.
    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport);
    } else {
      map.setCenter(place.geometry.location);
      map.setZoom(17); // Why 17? Because it looks good.
    }

    marker.setPosition(place.geometry.location);
    marker.setVisible(true);
  });
};

/**
 * Resets the circle geofence
 */
export const resetCircleGeofence = () => {
  if (circleGeofence) {
    circleGeofence.setMap(null);
  }
};

/**
 * Resets the polygon geofence
 */
export const resetPolygonGeofence = () => {
  if (polygonGeofence) {
    polygonGeofence.setMap(null);
  }
};

/**
 * This function draws a circle on the map that represents a geofence
 */
export const drawCircle = (
  map: google.maps.Map,
  mapsApi: typeof google.maps,
  center: Coords,
  edge: Coords,
  radius: number
) => {
  if (!map || !mapsApi) {
    return null;
  }

  resetCircleGeofence();

  circleGeofence = new mapsApi.Circle({
    map,
    center,
    radius: edge ? getCircleRadius(center, edge) : radius,
    ...MAP_SETTINGS,
  });

  return circleGeofence;
};

/**
 * This function draws a polygon on the map that represents a geofence. Because the backend renders the circle geofence
 * as multiple points (basically like a polygon), this function should be used to render a circle geofence coming
 * from the database
 */
export const drawPolygon = (map: google.maps.Map, mapsApi: typeof google.maps, points: Coords[]) => {
  if (!map || !mapsApi) {
    return null;
  }

  resetPolygonGeofence();

  polygonGeofence = new mapsApi.Polygon({
    map,
    paths: points,
    ...MAP_SETTINGS,
  });

  return polygonGeofence;
};

/**
 * Given an array of markers, this function returns the hull points (no points in the middle)
 */
export const getPolygonHullPoints = (mapsApi, points) => {
  let processedPoints;

  if (mapsApi) {
    const { LatLng } = mapsApi;
    processedPoints = points.map((point) => new LatLng(point.lat, point.lng));
  } else {
    return [];
  }

  processedPoints.sort(sortPointY);
  processedPoints.sort(sortPointX);

  const hullPoints = [];

  chainHull2D(processedPoints, processedPoints.length, hullPoints);

  return hullPoints.map((point) => ({
    lat: point.lat(),
    lng: point.lng(),
  }));
};

/**
 * Draws a placeholder geofence on the map to show the user how their geofence would look if they placed the next point.
 *
 * For circles, the first click indicates the center while the cursor position would indicate the edge of the circle.
 *
 * For polygons, the draw would only happen if two points are already on the map. At that point, the cursor position
 * would indicate the "3rd point".
 */
export const drawPlaceholder = (map, mapsApi, points, ovalTool, event) => {
  // The following conditions mean we really don't want to draw a placeholder
  if (!map || !mapsApi || (ovalTool && points.length !== 1) || (!ovalTool && points.length < 2)) {
    return;
  }

  const cursorPosition = {
    lat: event.latLng.lat(),
    lng: event.latLng.lng(),
  };

  // Used to call the function recursively as the mousemove does not trigger inside the geofence
  const recursiveEventFunction = (placeholderEvent) => {
    drawPlaceholder(map, mapsApi, points, ovalTool, placeholderEvent);
  };

  if (ovalTool) {
    // @ts-expect-error TS(2554): Expected 5 arguments, but got 4.
    const mapCircle = drawCircle(map, mapsApi, points[0], cursorPosition);

    // The regular mousemove will not trigger inside the geofence. We need
    // another listener to shrink it
    if (mapCircle) {
      mapCircle.addListener('mousemove', recursiveEventFunction);
    }
  }
};

export const getPolygonBounds = (emptyBounds, polygon) => {
  polygon.getPath().forEach((element) => {
    emptyBounds.extend(element);
  });

  return emptyBounds;
};

export const googleMapsCoordsToArray = (point: Coords): [number, number] => {
  return [point.lng, point.lat];
};

export const arrayToGoogleMapsCoords = (coords: [number, number]): Coords => {
  return {
    lat: coords[1],
    lng: coords[0],
  };
};
